<template>
  <div class="uk-modal-dialog uk-modal-body uk-margin-auto-vertical form uk-width-3-4">
    <Form @submit="onBeforeSubmit" :validation-schema="validationSchema" ref="form" :initial-values="formValues">
      <div class="uk-modal-header form-header">
        <span class="uk-text-bold uk-text-large">メール新規作成</span>
      </div>
      <div class="uk-modal-body">
        <div>
          <FormError :errors="globalErrors" />
          <div class="field">
            <FieldLabel title="テンプレート" :required="true" />
            <Field name="type" v-slot="{ field }">
              <select class="uk-select" v-bind="field" @change="onTemplateSelect">
                <option value=""></option>
                <option v-for="item in templates" :key="item.emailTemplateId" :value="item.emailTemplateId">
                  {{ item.name }}
                </option>
              </select>
            </Field>
            <FieldError name="type" />
          </div>
          <div class="field">
            <Field name="subject" v-slot="{ field }">
              <FieldLabel title="件名" :required="true" />
              <input class="uk-input" type="text" v-bind="field" @keydown.enter.prevent />
            </Field>
            <FieldError name="subject" />
          </div>
          <div class="field">
            <FieldLabel title="送信先" />
            <div>受検者 ({{ targets.length }})</div>
          </div>
          <div class="field">
            <Field name="message" v-slot="{ field }">
              <FieldLabel title="テンプレート本文" :required="true" />
              <textarea class="uk-input" rows="4" cols="60" v-bind="field"></textarea>
            </Field>
            <FieldError name="message" />
          </div>
        </div>
      </div>
      <div class="uk-modal-footer uk-text-right form-action">
        <div>
          <ActionButton title="戻る" class="uk-modal-close" toggleTarget=".uk-modal.send-mail" />
        </div>
        <div class="form-action-main">
          <ActionButton title="送信" color="primary" type="submit" :disabled="processing" />
        </div>
      </div>
    </Form>
  </div>
</template>
<style lang="scss" scoped>
@include modal-form;
.column-2 {
  display: flex;
  justify-content: center;
  div:first-child {
    flex: 1;
  }
}
.indent-1 {
  margin-left: $spacing-medium;
}
.participants-container {
  width: 300px;
  max-height: 450px;
  min-height: 200px;
  margin-left: $spacing-medium;
  .user-selector-container {
    height: 100%;
  }
  text-align: right;
}
.participants {
  margin-top: $spacing-small;
  height: 100%;
}
.datetime-field {
  display: flex;
  select {
    margin-left: $spacing-small;
    margin-right: $spacing-minimum;
  }
  div:last-child {
    display: flex;
    align-items: center;
  }
}
.uk-modal-dialog {
  width: unset;
}
textarea.uk-input {
  resize: vertical;
  min-height: 250px;
  line-height: unset;
}
</style>
<script lang="ts">
import { Options } from "vue-class-component";
import { Prop } from "vue-property-decorator";
import { Field, Form } from "vee-validate";
import FieldLabel from "../atoms/FieldLabel.vue";
import * as yup from "yup";
import FieldError from "../atoms/FieldError.vue";
import FormError from "../atoms/FormError.vue";
import ActionButton from "@/components/atoms/ActionButton.vue";
import { AsApi, EmailTemplateTypeEnum, GetEmailTemplate, ClientApi, AsBroadcastTypeEnum } from "../../autogen";
import { SelectableProfile } from "../../types";
import UserSelector from "../molecules/UserSelector.vue";
import DepartmentUserSelector from "../organisms/DepartmentUserSelector.vue";
import Datepicker from "vue3-datepicker";
import { ja } from "date-fns/locale";
import FormMixin from "../../helpers/mixins/form";
type FormValues = { type: string; subject: string; message: string };

@Options({
  components: {
    Field,
    Form,
    FieldLabel,
    FieldError,
    FormError,
    ActionButton,
    Datepicker,
    DepartmentUserSelector,
    UserSelector
  }
})
export default class SendMailForm extends FormMixin {
  @Prop({ type: String }) type: EmailTemplateTypeEnum = EmailTemplateTypeEnum.As;
  @Prop({ type: Array }) targets: string[] = [];
  templates: GetEmailTemplate[] = [];
  currentTemplate?: Partial<GetEmailTemplate> = {};
  submissionMessage = "送信しました";
  formValues: FormValues = {
    type: "",
    subject: "",
    message: ""
  };
  locale = ja;
  selectedUsers: SelectableProfile[] = [];
  @Prop({ type: String }) broadcastType!: AsBroadcastTypeEnum;
  validationSchema!: yup.AnyObjectSchema;
  beforeCreate() {
    this.validationSchema = yup.object({
      type: yup.string().required(this.$t("input_required", { field: "テンプレート" })),
      subject: yup
        .string()
        .trim()
        .required(this.$t("input_required", { field: "件名" })),
      message: yup
        .string()
        .trim()
        .required(this.$t("input_required", { field: "テンプレート本文" }))
    });
  }
  async beforeMount() {
    const templatesResponse = await new ClientApi().getEmailTemplatesByType({
      emailTemplateType: this.type
    });
    this.templates = templatesResponse.data.map(d => Object.assign(d, { message: d.message.replace(/\\n/g, "\n") }));
  }
  onTemplateSelect(event: Event) {
    const target = event.target as HTMLInputElement;
    if (!target.value) {
      return (this.formValues = {
        type: "",
        subject: "",
        message: ""
      });
    }
    this.formValues.message = this.templates.find(t => t.emailTemplateId === target.value)!.message;
  }
  async onBeforeSubmit(values: FormValues) {
    await this.onSubmit(async () => {
      const response = await new AsApi().batchPostSendTask({
        asSendTaskBatchSendRequest: {
          asSendTaskIds: this.targets,
          broadcastType: this.broadcastType,
          emailSubject: values.subject,
          emailMessage: values.message
        }
      });
      const sendIds = response.data.asSendTaskIds;
      let errUsers: string[] = [];
      if (sendIds && this.targets.length > sendIds.length) {
        errUsers = this.targets.filter(user => sendIds.indexOf(user) == -1);
      }
      this.$emit("submitted", errUsers);
    });
  }
}
</script>
